import { useState } from "react";
import { MapPin, Plus, Trash2, Crosshair, Users2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { TimeInput } from "@/components/TimeInput";
import { StaffMini } from "@/components/map/StaffMarkers";
import { unitPosts, hm, isMine } from "@/lib/staff";

const UnitChip = ({ u, n, active, onClick }) => (
  <button onClick={onClick} data-testid={`assign-unit-${u.key}`}
    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-left transition-colors ${active ? "bg-sky-500/20 border-sky-400 text-white" : "bg-[#0b1329] border-white/10 text-slate-300 hover:border-sky-500/50"}`}>
    <StaffMini group={u.group} size={16} />
    <span className="leading-tight">
      <span className="block font-mono text-xs font-bold">{u.tip ? `TIP ${u.tip}` : u.id}</span>
      <span className="block text-[10px] text-slate-500">{u.short} · {u.shiftHm}</span>
    </span>
    <span className={`ml-1 text-[10px] font-mono px-1.5 rounded-full ${n ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid={`assign-unit-count-${u.key}`}>{n}</span>
  </button>
);

const PostRow = ({ a, i, canEdit, onChange, onDelete, onFocus }) => (
  <div className="bg-[#0b1329] border border-white/10 rounded-lg p-3 space-y-2" data-testid={`assign-post-${i}`}>
    <div className="flex items-center gap-2">
      <span className="w-6 h-6 rounded-full bg-sky-500 text-slate-950 text-xs font-bold grid place-items-center shrink-0">{i + 1}</span>
      <Input value={a.place || ""} disabled={!canEdit} onChange={(e) => onChange({ place: e.target.value })} placeholder="Ubicación (p. ej. Cruce Rúa Real)"
        className="h-8 text-xs flex-1" data-testid={`assign-post-place-${i}`} />
      <span className="text-[10px] text-slate-500 font-mono">HORA</span>
      <TimeInput value={a.time} onChange={(v) => onChange({ time: v?.target ? v.target.value : v })} disabled={!canEdit}
        className={`h-8 w-[84px] text-xs ${a.time ? "" : "border-amber-500"}`} data-testid={`assign-post-time-${i}`} />
      <button onClick={onFocus} title="Ver en el mapa" className="p-1.5 text-slate-400 hover:text-sky-300" data-testid={`assign-post-focus-${i}`}><MapPin className="w-4 h-4" /></button>
      {canEdit && <button onClick={onDelete} title="Quitar" className="p-1.5 text-slate-400 hover:text-red-300" data-testid={`assign-post-delete-${i}`}><Trash2 className="w-4 h-4" /></button>}
    </div>
    <Textarea value={a.notes || ""} disabled={!canEdit} onChange={(e) => onChange({ notes: e.target.value })} rows={2}
      placeholder="Órdenes específicas para este puesto (aparecen en su hoja de servicio)" className="text-xs min-h-[52px]" data-testid={`assign-post-notes-${i}`} />
    {a.start != null ? <div className="text-[10px] font-mono text-slate-500">En el punto {hm(a.start)}–{hm(a.end)}</div>
      : <div className="text-[10px] text-amber-300" data-testid={`assign-post-notime-${i}`}>Sin hora: no aparece en el mapa ni en el plan hasta que la indique.</div>}
  </div>
);

export const AssignPanel = ({ units, assignments, anchor, selKey, setSelKey, armed, setArmed, canEdit, onChange, onFocus }) => {
  const [q, setQ] = useState("");
  const unit = units.find((u) => u.key === selKey);
  const timed = unit ? unitPosts(unit, assignments, anchor, units) : [];
  const untimed = unit ? assignments.filter((a) => isMine(a, unit, units) && !timed.some((t) => t.id === a.id)) : [];
  const count = (u) => assignments.filter((a) => isMine(a, u, units)).length;
  const posts = [...timed, ...untimed];
  const patch = (id, p) => onChange(assignments.map((a) => (a.id === id ? { ...a, ...p } : a)));
  const shown = units.filter((u) => !q || `${u.tip} ${u.id} ${u.short}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="bg-[#111c38] border border-white/10 rounded-xl p-5" data-testid="assign-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Users2 className="w-4 h-4 text-sky-400" />
          <span className="font-mono text-[10px] tracking-widest text-sky-400">ASIGNACIÓN DE EFECTIVOS</span>
          <span className="text-[11px] text-slate-500">{units.length} de alta en el briefing · {units.filter((u) => count(u)).length} con puesto</span>
        </div>
        {units.length > 8 && <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar TIP…" className="h-8 w-40 text-xs" data-testid="assign-search-input" />}
      </div>
      {!units.length ? (
        <div className="text-xs text-slate-400 border border-dashed border-white/10 rounded-lg p-4" data-testid="assign-empty">Dé de alta el personal (con su TIP) en la pestaña <b>Briefing</b> y aparecerá aquí para colocarlo en el mapa.</div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="assign-units-list">
          {shown.map((u) => <UnitChip key={u.key} u={u} n={count(u)} active={u.key === selKey} onClick={() => { setSelKey(u.key === selKey ? null : u.key); setArmed(false); }} />)}
        </div>
      )}
      {unit && (
        <div className="mt-4 border-t border-white/10 pt-4 space-y-3 rise" data-testid="assign-unit-detail">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-white font-semibold flex items-center gap-2"><StaffMini group={unit.group} size={18} />{unit.tip ? `TIP ${unit.tip}` : unit.id}
              <span className="text-xs font-normal text-slate-400">{unit.label} · turno {unit.shiftHm}{unit.shiftLabel ? ` · ${unit.shiftLabel}` : ""}</span></div>
            <div className="flex gap-2">
              {canEdit && (
                <button onClick={() => setArmed(!armed)} data-testid="assign-on-map-button"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${armed ? "bg-amber-400 text-slate-950" : "bg-sky-500 text-slate-950 hover:bg-sky-400"}`}>
                  {armed ? <><Crosshair className="w-4 h-4" /> Pulse en el mapa…</> : <><Plus className="w-4 h-4" /> Asignar ubicación</>}
                </button>
              )}
              <button onClick={() => { setSelKey(null); setArmed(false); }} className="p-1.5 text-slate-400 hover:text-white" data-testid="assign-close-button"><X className="w-4 h-4" /></button>
            </div>
          </div>
          {!posts.length && <div className="text-xs text-slate-400" data-testid="assign-no-posts">Sin ubicaciones. Pulse «Asignar ubicación» y luego el punto del mapa donde debe estar.</div>}
          {posts.map((a, i) => (
            <PostRow key={a.id} a={a} i={i} canEdit={canEdit} onChange={(p) => patch(a.id, p)}
              onDelete={() => onChange(assignments.filter((x) => x.id !== a.id))} onFocus={() => onFocus(a.loc)} />
          ))}
        </div>
      )}
    </div>
  );
};
